// @ts-nocheck

import { create } from "zustand";
import { cardsByRole } from "../utils/cardsByRole";
import { devtools } from "zustand/middleware";
import { CostCenterOption } from "../components/cards/cardsTypes";
import { PinData } from "../@types/mapTypes";

interface Card {
  id: string;
  label: string;
  checked: boolean;
  value: string;
}

type Role = "supply" | "leader" | "dev" | "";

interface CardsByRole {
  [key: string]: Card[];
}

interface User {
  nome: string;
  role: string[];
  firstLogin: bool;
  email: string;
}

interface ShowOffBtnMap {
  [key: string]: boolean;
}

interface MaxSpeedInfo {
  speed: number;
  latitude: number;
  longitude: number;
}

interface CarPlate {
  value: string;
  label: string;
  plate: string;
}

interface RouteGPS {
  allPings: PinData[];
  pingsInsideClockIns: PinData[];
  pingsOutsideClockIns: PinData[];
  clockIns?: Batida[];
  employeeHome?: Residencia;
}

export interface EmployeeClockIns {
  nome: string;
  data: string;
  hora: string;
  tipo_batida: string;
  latitude: number;
  longitude: number;
}

interface SelectProps {
  value: string;
  label: string;
}

interface Store {
  user: User;
  setUser: (newUser: User) => void;
  cards: Card[];
  setCards: (newCards: Card[]) => void;
  activeCards: string[];
  setActiveCards: (activeCards: string[]) => void;
  showOffBtnMap: ShowOffBtnMap;
  setShowOffBtnMap: (showOffBtnMapKey: keyof ShowOffBtnMap) => void;
  centerCost: CenterCost;
  setCenterCost: (newCenterCost: CenterCost) => void;
  selecionaPlacaCarro: SelectProps;
  setSelecionaPlacaCarro: (novaPlacaCarro: SelectProps) => void;
  totalDistance: number;
  setTotalDistance: (newTotalDistance: number) => void;
  maxSpeedInfo: MaxSpeedInfo;
  setMaxSpeedInfo: (newMaxSpeedInfo: MaxSpeedInfo) => void;
  transitTimeInsideClockIns: number;
  setTransitTimeInsideClockIns: (newTransitTime: number) => void;
  totalTimeOutsideClockIns: number;
  setTotalTimeOutsideClockIns: (newOutTime: number) => void;
  totalDistanceBetweenClockIns: number;
  setTotalDistanceBetweenClockIns: (newWorkDistance: number) => void;
  distanceOutsideClockIns: number;
  setDistanceOutsideClockIns: (newOutDistance: number) => void;
  carPlate: CarPlate;
  setCarPlate: (newCarPlate: CarPlate) => void;
  transitTimeOutsideClockIns: number;
  setTransitTimeOutsideClockIns: (newOutTime: number) => void;
  totalStationaryWorkTime: number;
  setTotalStationaryWorkTime: (newOutTime: number) => void;
  routeGPS: RouteGPS;
  setGpsPings: (newGpsPings: RouteGPS[]) => void;
  employeeClockIns: EmployeeClockIns[];
  setEmployeeClockIns: (newEmployeeClockIns: EmployeeClockIns[]) => void;
  trackingDate: Date;
  setTrackingDate: (newTrackingDate: Date) => void;
  selectedEmployee: CostCenterOption | null;
  setSelectedEmployee: (newSelectedEmployee: CostCenterOption | null) => void;
}

const typedCardsByRole: CardsByRole = cardsByRole;

type CenterCost = {
  value: string;
  label: string;
} | null;

const saveToStorage = <T>(key: string, value: T): void => {
  const storedData = localStorage.getItem("@tlp-1.0.0");
  const parsedData = storedData ? { ...JSON.parse(storedData) } : {};
  parsedData[key] = value;
  localStorage.setItem("@tlp-1.0.0", JSON.stringify(parsedData));
};

const loadFromStorage = <T>(key: string): T | null => {
  const storedData = localStorage.getItem("@tlp-1.0.0");
  if (!storedData) return null;
  const parsedData = JSON.parse(storedData);
  return parsedData[key] as T;
};

const useStore = create<Store>()(
  devtools(
    (set, get) => ({
      selectedEmployee: null,
      setSelectedEmployee: (newSelectedEmployee: CostCenterOption | null) =>
        set({ selectedEmployee: newSelectedEmployee }),
      trackingDate: new Date(),
      setTrackingDate: (newTrackingDate: Date) => set({ trackingDate: newTrackingDate }),
      employeeClockIns: [],
      setEmployeeClockIns: (newEmployeeClockIns: EmployeeClockIns[]) =>
        set({ employeeClockIns: newEmployeeClockIns }),
      routeGPS: {
        allPings: [],
        pingsInsideClockIns: [],
        pingsOutsideClockIns: [],
        clockIns: [],
        employeeHome: null,
      },
      setGpsPings: (newGpsPings: RouteGPS) => set({ routeGPS: newGpsPings }),
      totalStationaryWorkTime: 0,
      setTotalStationaryWorkTime: (newTotalStationaryWorkTime: number) =>
        set({ totalStationaryWorkTime: newTotalStationaryWorkTime }),
      transitTimeOutsideClockIns: 0,
      setTransitTimeOutsideClockIns: (newTransiteTimeOutClockIns: number) =>
        set({ transitTimeOutsideClockIns: newTransiteTimeOutClockIns }),
      totalTimeOutsideClockIns: 0, //  tempo fora da batida de pontos em horas (apenas em movimento)
      setTotalTimeOutsideClockIns: (newOutTime: number) =>
        set({ totalTimeOutsideClockIns: newOutTime }),
      totalDistanceBetweenClockIns: 0, // distancia dentro da batida de pontos
      setTotalDistanceBetweenClockIns: (newWorkDistance: number) =>
        set({ totalDistanceBetweenClockIns: newWorkDistance }),
      distanceOutsideClockIns: 0,
      setDistanceOutsideClockIns: (newOutDistance: number) =>
        set({ distanceOutsideClockIns: newOutDistance }),
      transitTimeInsideClockIns: 0,
      setTransitTimeInsideClockIns: (newTransitTime: number) =>
        set({ transitTimeInsideClockIns: newTransitTime }),
      maxSpeedInfo: { speed: 0, latitude: 0, longitude: 0 },
      setMaxSpeedInfo: (newMaxSpeedInfo: MaxSpeedInfo) =>
        set({ maxSpeedInfo: newMaxSpeedInfo }),
      totalDistance: 0,
      setTotalDistance: (newTotalDistance: number) =>
        set({ totalDistance: newTotalDistance }),
      centerCost: null,
      setCenterCost: (newCenterCost: CenterCost) => set({ centerCost: newCenterCost }),
      showOffBtnMap: {
        polylineAllPings: true,
        polylineInsideClockIns: false,
        polylineOutsideClockIns: false,
        marker: true,
        stopPoints: true,
        branches: true,
      },
      setShowOffBtnMap: (key) => {
        const currentShowOffBtnMap = get().showOffBtnMap;
        set({
          showOffBtnMap: {
            ...currentShowOffBtnMap,
            [key]: !currentShowOffBtnMap[key],
          },
        });
      },
      cards: [],
      setCards: (newCards: Card[]) => {
        set({ cards: newCards });
        saveToStorage("cards", newCards);
      },
      user: { nome: "", role: [], firstLogin: false, email: "" },
      setUser: (newUser: User) => set({ user: newUser }),
      activeCards: [],
      setActiveCards: (activeCards: string[]) => set({ activeCards }),
      carPlate: { value: "", label: "", plate: "" },
      setCarPlate: (newCarPlate: CarPlate) => set({ carPlate: newCarPlate }),
      selecionaPlacaCarro: null,
      setSelecionaPlacaCarro: (novaPlacaCarro: SelectProps) =>
        set({ selecionaPlacaCarro: novaPlacaCarro }),
    }),
    {
      enabled: true,
      name: "Card Store",
    }
  )
);

export default useStore;
